import { useFusionAuth } from "@fusionauth/react-sdk";
import {
  Autocomplete,
  type AutocompleteValue,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import React, { useContext, useEffect, useMemo, useState } from "react";
import CloseArrowTooltip from "../../../components/CloseArrowTooltip";
import {
  ProjectContext,
  ProjectDispatchContext,
} from "../../../contexts/ProjectContext";
import {
  convertProjectContextToProject,
  convertProjectToProjectContext,
} from "../../../models/Project.ts";
import { useFetch } from "../../../services/hooks/useFetch.ts";
import { apiPrefix, deviceApiPrefix } from "../../../utils/main.ts";

export default function DeviceDetailsSettings() {
  const [editMode, setEditMode] = useState(false);
  const project: ProjectContextType | null = useContext(ProjectContext);
  const dispatch = useContext(ProjectDispatchContext);
  const { update: updateProject } = useFetch<
    undefined,
    ProjectRequest,
    Project
  >();

  const [reasonDetails, setReasonDetails] = useState<string>("");

  const [displayErrors, setDisplayErrors] = useState<boolean>(false);

  const { userInfo } = useFusionAuth();
  const isAdmin = userInfo?.roles?.includes("ADMIN");
  const isWorkshop = userInfo?.roles?.includes("WORKSHOP");
  const isUser = userInfo?.roles?.includes("USER");

  const { data: gmdns, fetch: fetchGmdns } = useFetch<Gmdn[]>();
  const [selectedGmdn, setSelectedGmdn] = useState(project?.deviceGmdnId);

  const [selectedManufacturer, setSelectedManufacturer] = useState<
    DeviceManufacturer | undefined
  >(project?.devices ? project?.devices[0].deviceManufacturer : undefined);
  const { data: deviceManufacturers, fetch: getDeviceManufacturers } =
    useFetch<DeviceManufacturer[]>();

  const { data: devicesFromGmdn, fetch: fetchDevicesFromGmdn } =
    useFetch<Device[]>();
  const [deviceOptions, setDeviceOptions] = useState<Set<Device>>(
    new Set<Device>(),
  );
  const [selectedDevices, setSelectedDevices] = useState<Set<Device>>(
    new Set<Device>(project?.devices || []),
  );

  const handleDeviceSelectionChange = function (
    _event: React.SyntheticEvent,
    deviceList: AutocompleteValue<Device, true, false, true>,
  ) {
    const updatedSet = new Set<Device>();
    deviceList.forEach((value) => {
      if (typeof value === "string") {
        // This can happen when the user has cleared their selection or
        // has typed a value that does not match the options.  There is
        // nothing to change for these entries.
      } else {
        updatedSet.add(value);
      }
    });
    setSelectedDevices(updatedSet);
  };

  const handleChangeManufacturers = function (
    _event: React.SyntheticEvent,
    value: DeviceManufacturer | null,
  ) {
    if (value === null) {
      setSelectedManufacturer(undefined);
    } else {
      setSelectedManufacturer(value);
    }
  };

  const fetchSuggestions = useMemo(
    () =>
      debounce((searchValue) => {
        if (searchValue) {
          void getDeviceManufacturers(
            `${deviceApiPrefix}/devices/manufacturers?search=~${searchValue}~`,
          );
        }
      }, 500),
    [getDeviceManufacturers],
  );

  useEffect(() => {
    // load GMDN options
    if (isWorkshop && selectedManufacturer?.id) {
      void fetchGmdns(
        `${deviceApiPrefix}/devices/manufacturers/${selectedManufacturer.id}/categories`,
      );
    } else {
      void fetchGmdns(`${deviceApiPrefix}/categories/`);
    }
  }, [fetchGmdns, isWorkshop, selectedManufacturer?.id]);

  useEffect(() => {
    // load initial manufacturer options
    if (isWorkshop) {
      void getDeviceManufacturers(
        `${deviceApiPrefix}/devices/manufacturers?search=~~`,
      );
    } else {
      void getDeviceManufacturers(
        `${deviceApiPrefix}/devices/manufacturers/company/`,
      );
    }
  }, [isWorkshop, getDeviceManufacturers]);

  useEffect(() => {
    if (!isWorkshop) {
      if (selectedGmdn) {
        void fetchDevicesFromGmdn(
          `${deviceApiPrefix}/categories/${selectedGmdn}/devices`,
        );
      }
    } else if (selectedManufacturer?.id && selectedGmdn) {
      void fetchDevicesFromGmdn(
        `${deviceApiPrefix}/devices/manufacturers/${selectedManufacturer.id}/categories/${selectedGmdn}`,
      );
    }
  }, [
    selectedManufacturer,
    fetchDevicesFromGmdn,
    gmdns,
    isWorkshop,
    selectedGmdn,
  ]);

  useEffect(() => {
    const newDevices = new Set<Device>();
    // add all options for the current gmdn selection
    devicesFromGmdn?.forEach((value) => {
      newDevices.add(value);
    });
    // add all current devices in case they're from a different gmdn
    selectedDevices.forEach((device) => {
      newDevices.add(device);
    });
    setDeviceOptions(newDevices);
  }, [devicesFromGmdn, selectedDevices]);

  return (
    <Grid container direction={"column"} className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Device Details</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">
            Information about the medical device(s) included in this project.
          </Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">* Required fields</Typography>
        </Grid>
        <br />
        {!isUser && (
          <>
            <Grid>
              {editMode && isWorkshop ? (
                <FormControl
                  error={displayErrors && !selectedManufacturer?.id}
                  sx={{ width: "50ch" }}
                >
                  <Autocomplete<DeviceManufacturer>
                    filterOptions={(x) => x}
                    disablePortal
                    value={selectedManufacturer}
                    options={deviceManufacturers || []}
                    getOptionLabel={(option) => option.name || ""}
                    onChange={handleChangeManufacturers}
                    isOptionEqualToValue={(o, v) => v === null || o.id === v.id}
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") {
                        fetchSuggestions(value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Manufacturer *" />
                    )}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      );
                    }}
                  />
                  <FormHelperText>
                    {displayErrors && !selectedManufacturer
                      ? "This field is required"
                      : ""}
                  </FormHelperText>
                </FormControl>
              ) : (
                <>
                  <Typography variant="caption">Manufacturer *</Typography>
                  <Typography variant="body1">
                    {selectedManufacturer?.id
                      ? selectedManufacturer?.name
                      : "None"}
                  </Typography>
                </>
              )}
            </Grid>
            <br />
            <Grid>
              {editMode ? (
                <FormControl
                  error={displayErrors && !selectedGmdn}
                  sx={{ width: "50ch" }}
                >
                  <InputLabel shrink htmlFor="select-categories" required>
                    GMDN Category
                  </InputLabel>
                  <Select
                    variant="outlined"
                    value={selectedGmdn}
                    label={"GMDN Category"}
                    readOnly={!editMode}
                    onChange={(event) => {
                      setSelectedGmdn(event.target.value);
                    }}
                    inputProps={{ id: "select-categories" }}
                  >
                    <MenuItem value={-1}>N.A.</MenuItem>
                    {gmdns?.map((json) => (
                      <MenuItem key={json.id} value={json.id}>
                        {json.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {displayErrors && !selectedGmdn
                      ? "This field is required"
                      : ""}
                  </FormHelperText>
                </FormControl>
              ) : (
                <>
                  <Typography variant="caption">GMDN Category *</Typography>
                  <Typography variant="body1">
                    {selectedGmdn
                      ? gmdns?.find((g) => g.id === selectedGmdn)?.name
                      : "None"}
                  </Typography>
                </>
              )}
            </Grid>
            <br />
          </>
        )}
        <Grid>
          {editMode ? (
            <FormControl error={displayErrors && selectedDevices.size === 0}>
              <InputLabel shrink htmlFor="select-devices" required>
                Device
              </InputLabel>
              <Autocomplete<Device, true, false, true>
                filterSelectedOptions
                multiple
                freeSolo
                id={"select-devices"}
                readOnly={!editMode}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={[...deviceOptions]}
                value={[...selectedDevices]}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.description || ""
                }
                getOptionKey={(option) =>
                  typeof option === "string" ? option : option.id || ""
                }
                data-testid="DeviceDetailSettings-autocompletedevice"
                onChange={handleDeviceSelectionChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="medium"
                    variant="outlined"
                    label={"Device"}
                    required
                  />
                )}
                sx={{ width: "50ch" }}
              />
              <FormHelperText>
                {displayErrors && selectedDevices.size === 0
                  ? "This field is required"
                  : ""}
              </FormHelperText>
            </FormControl>
          ) : (
            <>
              <Typography variant="caption">Device *</Typography>
              <Typography variant="body1">
                {Array.from(selectedDevices.values())
                  .map((value) => value.description)
                  .toString()
                  .replaceAll(",", ", ")}
              </Typography>
            </>
          )}
        </Grid>
        <br />
        {editMode && (
          <>
            <br />
            <Grid>
              <TextField
                required
                variant="outlined"
                label="Note/Reason for Updating the Device Details"
                className="settings-text-input"
                sx={{ width: "50ch" }}
                value={reasonDetails}
                error={displayErrors && reasonDetails === ""}
                helperText={
                  displayErrors && reasonDetails === ""
                    ? "This field is required"
                    : ""
                }
                onChange={(event) => {
                  setReasonDetails(event.target.value);
                }}
              />
            </Grid>
          </>
        )}
        <br />
        <Grid container justifyContent={"right"}>
          {(editMode && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  if (project && project.devices?.length) {
                    setSelectedManufacturer(
                      project.devices[0].deviceManufacturer,
                    );
                  }
                  setSelectedGmdn(project?.deviceGmdnId);
                  setSelectedDevices(new Set<Device>(project?.devices || []));
                  setReasonDetails("");
                  setEditMode(false);
                  setDisplayErrors(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="update-button"
                sx={{ marginLeft: "5px" }}
                onClick={() => {
                  if (isAdmin) {
                    if (
                      selectedManufacturer?.id === null ||
                      selectedGmdn === undefined ||
                      selectedDevices.size === 0 ||
                      reasonDetails === ""
                    ) {
                      setDisplayErrors(true);
                      return;
                    } else if (
                      project?.devices &&
                      selectedDevices &&
                      JSON.stringify(project.devices) !==
                        JSON.stringify(selectedDevices)
                    ) {
                      project.devices = Array.from(selectedDevices.values());
                      // todo: update gmdn on project
                      void updateProject({
                        path: `${apiPrefix}/projects/${project?.id}`,
                        body: {
                          reason: reasonDetails,
                          project: convertProjectContextToProject(project),
                        },
                        responseManipulationFunction: (data) => {
                          convertProjectToProjectContext(data, dispatch);
                          return;
                        },
                      });
                    }

                    setDisplayErrors(false);
                    setEditMode(false);
                    setReasonDetails("");
                  }
                }}
              >
                Update
              </Button>
            </>
          )) ||
            (isAdmin && (
              <CloseArrowTooltip
                title={project?.isLocked ? "This project is locked." : ""}
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={() => setEditMode(true)}
                    disabled={project?.isLocked}
                  >
                    Edit
                  </Button>
                </span>
              </CloseArrowTooltip>
            ))}
        </Grid>
      </div>
    </Grid>
  );
}
